import { useState, useEffect } from 'react';

function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : null;
}

function toHex(r, g, b) {
  return `#${[r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('')}`;
}

function applyPrimaryColor(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return;
  const [r, g, b] = rgb;
  const mix  = (t) => toHex(r * t + 255 * (1 - t), g * t + 255 * (1 - t), b * t + 255 * (1 - t));
  const dark = (t) => toHex(r * t, g * t, b * t);
  const root = document.documentElement;
  root.style.setProperty('--color-primary-50',  mix(0.08));
  root.style.setProperty('--color-primary-100', mix(0.15));
  root.style.setProperty('--color-primary-200', mix(0.30));
  root.style.setProperty('--color-primary-300', mix(0.50));
  root.style.setProperty('--color-primary-400', mix(0.70));
  root.style.setProperty('--color-primary-500', hex);
  root.style.setProperty('--color-primary-600', dark(0.85));
  root.style.setProperty('--color-primary-700', dark(0.70));
  root.style.setProperty('--color-primary-800', dark(0.55));
  root.style.setProperty('--color-primary-900', dark(0.40));
}

// Logo de la fédération des CAE (client), affiché par défaut pour toutes les CAE
const LOGO_FEDE_URL = './assets/logo-fede-cae.webp';
const SITE_FEDE_URL = 'https://www.les-cae.coop';

function Header() {
  const [config, setConfig] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Charger la configuration CAE
  useEffect(() => {
    const loadConfig = async () => {
      const applyBranding = (color) => {
        if (color) {
          applyPrimaryColor(color);
        }
      };

      try {
        const response = await fetch('./config/config.json', { cache: 'no-store' });
        if (!response.ok) {
          throw new Error(`Impossible de charger config/config.json (${response.status})`);
        }
        const data = await response.json();
        applyBranding(data.branding?.couleur_primaire);
        setConfig(data);
      } catch (error) {
        console.error('Erreur chargement configuration:', error);
        // Configuration par défaut si erreur
        const fallbackConfig = {
          cae_info: {
            nom: 'CAE',
            logo_url: './assets/logo-cae.png',
            url_site: '#',
          },
          branding: {
            couleur_primaire: '#e5074d',
          },
          messages: {
            intro: 'Bienvenue sur le simulateur de revenus',
          },
        };
        applyBranding(fallbackConfig.branding.couleur_primaire);
        setConfig(fallbackConfig);
      }
    };

    loadConfig();
  }, []);

  if (!config) {
    return null; // Ou un skeleton loader
  }

  return (
    <header
      className="bg-white shadow-md"
      role="banner"
    >
      <div className="container mx-auto px-4 py-4 max-w-6xl">
        <div className="flex items-center justify-between">
          {/* Logo et nom de la CAE */}
          <div className="flex items-center gap-4">
            {config.options_affichage?.afficher_logo !== false && (
              <a
                href={config.cae_info.url_site}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0"
                aria-label={`Retour au site de ${config.cae_info.nom}`}
              >
                <img
                  src={config.cae_info.logo_url}
                  alt={`Logo ${config.cae_info.nom}`}
                  className="h-12 w-auto object-contain"
                  onError={(e) => {
                    // Image de fallback si le logo n'existe pas
                    e.target.style.display = 'none';
                  }}
                />
              </a>
            )}

            <div className="flex flex-col">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-heading">
                Simulateur de Revenus
              </h1>
              <p className="text-sm text-gray-600 hidden md:block">
                {config.cae_info.nom}
              </p>
            </div>
          </div>

          {/* Bouton Contact + logo de la fédération (client), sur une seule rangée */}
          <div className="flex items-center gap-3">
            {config.cae_info.email_contact && (
              <>
                <a
                  href={`mailto:${config.cae_info.email_contact}`}
                  className="hidden md:flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-700 hover:text-primary-800 border border-primary-300 rounded-lg hover:bg-primary-50 transition-colors"
                  aria-label="Nous contacter par email"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  <span>Contact</span>
                </a>
                <div className="hidden md:block h-8 w-px bg-gray-200" aria-hidden="true" />
              </>
            )}

            <a
              href={SITE_FEDE_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Site de la fédération des CAE"
            >
              <img
                src={LOGO_FEDE_URL}
                alt="Logo de la fédération des CAE"
                className="h-14 w-auto object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </a>
          </div>
        </div>

        {/* Message d'introduction — masqué au scroll */}
        {config.messages?.intro && (
          <div
            className="overflow-hidden transition-all duration-300 ease-in-out"
            style={{ maxHeight: scrolled ? 0 : '8rem', opacity: scrolled ? 0 : 1 }}
          >
            <div className="mt-2 md:mt-3 space-y-1">
              <p className="text-base md:text-lg text-gray-700 text-center md:text-left">
                {config.messages.intro}
              </p>
              {config.messages?.intro_description && (
                <p className="text-sm text-gray-600 text-center md:text-left">
                  {config.messages.intro_description}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

export default Header;
